import React, { useMemo } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { normalizeFieldPosition, resolveLayoutValue } from '../../utils/layoutUtils'
import WorkflowStageBanner from '../workflow/WorkflowStageBanner'

const MIN_CONTAINER_HEIGHT = 320
const CONTAINER_PADDING = 16

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const getContainerHeight = (layoutConfig) => {
  if (!Array.isArray(layoutConfig) || layoutConfig.length === 0) {
    return MIN_CONTAINER_HEIGHT
  }

  const maxBottom = layoutConfig.reduce((acc, field) => {
    const y = safeNumber(field?.y, 0)
    const height = safeNumber(field?.height, 64)
    const bottom = y + height
    return bottom > acc ? bottom : acc
  }, 0)

  return Math.max(maxBottom + CONTAINER_PADDING, MIN_CONTAINER_HEIGHT)
}


const isWorkflowStageBannerField = (field = {}) => {
  const type = String(field?.type || '').trim().toLowerCase()
  const role = String(field?.role || '').trim().toLowerCase()
  const name = String(field?.name || '').trim().toLowerCase()
  return (
    type === 'workflow_stage_banner' ||
    type === 'billing_stage_banner' ||
    role === 'workflow_stage_banner' ||
    name === 'workflow_stage_banner'
  )
}

const normalizeBannerConfig = (field = {}) => {
  const fieldConfig = field?.config && typeof field.config === 'object' ? field.config : {}
  const legacyConfig = {
    resolverKey: field?.resolverKey,
    mode: field?.mode,
    prefix: field?.prefix,
    showStageNumber: field?.showStageNumber,
    showQuotes: field?.showQuotes,
    shape: field?.shape,
    emptyStateMode: field?.emptyStateMode,
    emptyBehavior: field?.emptyBehavior,
    emptyMessage: field?.emptyMessage,
    customLabel: field?.customLabel,
    heading: field?.heading,
    title: field?.title,
    showMetrics: field?.showMetrics,
  }
  const config = { ...legacyConfig, ...fieldConfig }

  const resolverKey = config.resolverKey || 'workflow.current_stage'
  const mode =
    config.mode ||
    (resolverKey === 'workflow.current_billable_stage'
      ? 'billable_stage'
      : 'progress_stage')

  return {
    resolverKey,
    mode,
    prefix: config.prefix || 'ETAPA',
    showStageNumber: config.showStageNumber !== false,
    showQuotes: Boolean(config.showQuotes),
    shape: config.shape || 'rounded',
    emptyStateMode: config.emptyStateMode || config.emptyBehavior || 'message',
    emptyMessage: config.emptyMessage || '',
    customLabel: config.customLabel || config.heading || config.title || field?.label || '',
    showMetrics: config.showMetrics !== false,
  }
}

const fieldPaperStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  width: '100%',
  height: '100%',
  p: 1.25,
  overflow: 'hidden',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 1,
  backgroundColor: 'background.paper',
}

const ConsumerDetailLayoutRenderer = ({ layoutConfig = [], consumerData = null }) => {
  const normalizedLayout = useMemo(() => {
    if (!Array.isArray(layoutConfig)) return []

    return layoutConfig
      .filter((field) => field && typeof field === 'object')
      .map((field) => normalizeFieldPosition(field))
  }, [layoutConfig])

  const containerHeight = useMemo(
    () => getContainerHeight(normalizedLayout),
    [normalizedLayout]
  )

  if (!consumerData) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No hay consumidor seleccionado.
        </Typography>
      </Paper>
    )
  }

  if (!Array.isArray(normalizedLayout) || normalizedLayout.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No hay layout configurado para este producto.
        </Typography>
      </Paper>
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: containerHeight,
        backgroundColor: 'transparent',
      }}
    >
      {normalizedLayout.map((field) => {
        const value = resolveLayoutValue(field, consumerData)
        const isWorkflowBanner = isWorkflowStageBannerField(field)
        const bannerConfig = normalizeBannerConfig(field)

        return (
          <Box
            key={field.id || `${field.name}-${field.x}-${field.y}`}
            sx={{
              position: 'absolute',
              left: field.x,
              top: field.y,
              width: field.width,
              height: field.height,
            }}
          >
            {isWorkflowBanner ? (
              <WorkflowStageBanner
                workflowState={consumerData?.workflowPayload || null}
                resolvers={
                  consumerData?.workflowResolverPayload ||
                  consumerData?.workflowResolversRaw ||
                  consumerData?.workflow ||
                  null
                }
                config={bannerConfig}
              />
            ) : (
              <Paper sx={fieldPaperStyles}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.1,
                    mb: 0.5,
                    wordBreak: 'break-word',
                  }}
                >
                  {field.label}
                </Typography>

                <Typography
                  sx={{
                    fontSize: field.fontSize,
                    fontWeight: 500,
                    color: 'text.primary',
                    lineHeight: 1.25,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'normal',
                  }}
                >
                  {value || '—'}
                </Typography>
              </Paper>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default ConsumerDetailLayoutRenderer
